/**
 * 首次打开 AI 面板时展示的功能介绍轮播卡片。
 * 3 张静态图片 + 底部圆点指示器，自动轮播，点击指示器可切换。
 * 卡片整张可点击 = 直接把对应预设问题发到会话；
 * 展示哪几张由后端 onboarding_cards 裁决，前端只按 key 过滤。
 * 仅首次打开时由父组件控制渲染。
 */
import React, { useState, useEffect, useCallback, useRef } from 'react'
import { OnboardingCardItem } from './onboardingCardsCatalog'
import './OnboardingCarousel.scss'

const AUTO_INTERVAL = 3500

interface OnboardingCarouselProps {
  /**
   * 已合并后端 query + 本地素材的完整卡列表 (mergeOnboardingCards 输出)。
   * 顺序由后端决定; 空数组 → 不渲染整个轮播。
   */
  cards: OnboardingCardItem[]
  /**
   * 点击某张卡片 → 把对应预设问题发到会话。
   * 参数: (query, label, index1Based) — index 用于神策 number1 (1-based)。
   */
  onCardClick: (query: string, label: string, index1Based: number) => void
  /** disabled 时不响应点击 (例如上一轮还在 SSE 流式中) */
  disabled?: boolean
}

const OnboardingCarousel: React.FC<OnboardingCarouselProps> = ({
  cards,
  onCardClick,
  disabled
}) => {
  const [current, setCurrent] = useState(0)
  const timerRef = useRef<ReturnType<typeof setInterval>>()

  // 卡片数变化时 (理论只发生在初次加载) 把 current 限回有效范围
  useEffect(() => {
    if (current >= cards.length) setCurrent(0)
  }, [cards.length, current])

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current)
    // 仅 1 张时关掉自动轮播 (没意义还会引起 React state 更新)
    if (cards.length <= 1) return
    timerRef.current = setInterval(() => {
      setCurrent((i) => (i + 1) % cards.length)
    }, AUTO_INTERVAL)
  }, [cards.length])

  useEffect(() => {
    resetTimer()
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [resetTimer])

  const goTo = useCallback(
    (i: number) => {
      setCurrent(i)
      resetTimer()
    },
    [resetTimer]
  )

  const handleCardClick = (card: OnboardingCardItem, indexFromZero: number) => {
    if (disabled) return
    onCardClick(card.query, card.cardLabel, indexFromZero + 1)
  }

  // 所有 key 都被 gate 掉 → 整块不展示, 不留空容器
  if (cards.length === 0) return null

  return (
    <div className="onboarding-carousel">
      <div
        className="onboarding-carousel__track"
        style={{ transform: `translateX(-${current * 100}%)` }}
      >
        {cards.map((card, i) => (
          <button
            key={card.key}
            type="button"
            className="onboarding-carousel__slide"
            onClick={() => handleCardClick(card, i)}
            disabled={disabled}
            aria-label={card.cardLabel}
            title={card.cardLabel}
          >
            <img src={card.src} alt={card.cardLabel} draggable={false} />
          </button>
        ))}
      </div>
      {cards.length > 1 && (
        <div className="onboarding-carousel__dots">
          {cards.map((_, i) => (
            <span
              key={i}
              className={`onboarding-carousel__dot${
                i === current ? ' onboarding-carousel__dot--active' : ''
              }`}
              onClick={() => goTo(i)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default OnboardingCarousel
